/* eslint react/no-multi-comp: 0 */

// Vendor Libs
import React, {cloneElement, isValidElement} from 'react';
import {get, isArray, isEmpty, isString, last, map, sortBy, startCase} from 'lodash';
import classnames from 'classnames';
import {formatDate, formatTimezone} from '../core/date';

// Components
import BingeableIndicator from '../../components/app/bingeable-indicator';
import Icon from '../../components/core/icon';
import TooltipTrigger from '../../components/core/tooltip-trigger';
import FlaggedMetadataIndicator from '../../components/app/flagged-metadata-indicator';

// Styles
import './formatters.scss';

const getChildText = (rowChild) => (isValidElement(rowChild) ? get(rowChild, 'props.children') : rowChild || '');

const setChildText = (target, value) => (isValidElement(target) ? cloneElement(target, {children: value}) : value);

const setClassName = (target, value) => (isValidElement(target) ? cloneElement(target, {className: classnames(target.props.className, value)}) : target);

const lockedByIcon = (_rowChild, {locked, lockedBy}) => {
    if (locked) {
        const lockItemToolTip = () => <div className="formatters--tooltip">Locked by {lockedBy}</div>;

        return (
            <TooltipTrigger tooltipRenderer={lockItemToolTip}>
                <Icon fontType="fa" iconName="lock" />
            </TooltipTrigger>
        );
    }

    return null;
};

const lockedLink = (rowChild, {locked}) => {
    if (locked) {
        return getChildText(rowChild);
    }

    return rowChild;
};

const moviesAndProgramsTitle = (
    rowChild,
    {episodeCount, flaggedMetadata, mappedCounts = {}, roviProgramId, vfb, tmsId, sourceId},
    _column,
    _key
) => {
    const {DISH, SLING} = mappedCounts;
    const childText = getChildText(rowChild);
    let updatedRowChild = rowChild;

    if (isString(childText) && (episodeCount || mappedCounts)) {
        const titleContent = (
            <span>
                {childText}
                {episodeCount ? <div className="formatters--episode-count">Episodes: {episodeCount}</div> : null}
                {DISH || SLING ? (
                    <div className="formatters--mapped-count">
                        Mapped: D: {DISH}, S: {SLING}
                    </div>
                ) : null}
            </span>
        );

        updatedRowChild = setChildText(rowChild, titleContent);
    }

    updatedRowChild = setClassName(updatedRowChild, 'formatters--title');

    return (
        <>
            {!roviProgramId && !tmsId && !sourceId ? (
                <Icon className="formatters--shell-icon" fontType="mc" iconName="conch-shell" title="AMS shell" />
            ) : null}
            <BingeableIndicator vfb={vfb} />
            <FlaggedMetadataIndicator flaggedMetadata={flaggedMetadata} />
            {updatedRowChild}
        </>
    );
};

const mediaItemsTitle = (rowChild, {amsEpisode, amsFranchise, roviProgramId, tmsId, sourceId}) => {
    const isFranchiseShell = get(amsFranchise, 'echostarId') && !get(amsFranchise, 'roviProgramId') && !get(amsFranchise, 'tmsId') && !get(amsFranchise, 'sourceId');
    const isEpisodeShell = get(amsEpisode, 'echostarId') && !get(amsEpisode, 'roviProgramId') && !get(amsEpisode, 'tmsId') && !get(amsEpisode, 'sourceId');
    const vfb = get(amsFranchise, 'vfb');
    let shellTitle;

    if (isFranchiseShell && isEpisodeShell) {
        shellTitle = 'AMS Franchise & Episode shell';
    } else if (isFranchiseShell) {
        shellTitle = 'AMS Franchise shell';
    } else if (isEpisodeShell) {
        shellTitle = 'AMS Episode shell';
    }

    return (
        <>
            {shellTitle && !roviProgramId && !tmsId && !sourceId ? (
                <Icon className="formatters--shell-icon" fontType="mc" iconName="conch-shell" title={shellTitle} />
            ) : null}
            <BingeableIndicator vfb={vfb} />
            {setClassName(rowChild, 'formatters--title')}
        </>
    );
};

const roles = (_rowChild, {scope}) => {
    const roles = isArray(scope) ? scope : [];

    return map(sortBy(roles), (role, index) => {
        const styles = {display: 'inline-block', padding: '0 5px'};

        if (roles.length > index + 1) {
            styles.borderRight = '1px solid #ccc';
        }

        return (
            <div key={index} style={styles}>
                {startCase(role)}
            </div>
        );
    });
};

const issues = (_rowChild, {issues}) => map(isArray(issues) ? sortBy(issues) : [], ({fieldName: issue}, index) => (
        <div className="formatters--issue-list-item" key={index}>
            {startCase(issue)}
        </div>
    ));

const timestamp = (_rowChild, {timestamp}) => formatTimezone(timestamp);

const lastUpdated = (_rowChild, {lastUpdated}) => formatDate(lastUpdated);

const amsProgramLastUpdate = (rowChild, {amsPrograms}) => (!isEmpty(amsPrograms) ? formatDate(get(last(sortBy(amsPrograms, 'lastUpdate')), 'lastUpdate')) : rowChild);

const amsProgramLastUpdateDetail = (rowChild, {amsPrograms}) => (!isEmpty(amsPrograms) ? formatTimezone(get(last(sortBy(amsPrograms, 'lastUpdate')), 'lastUpdate')) : rowChild);

const amsProgramTitle = (rowChild, {amsPrograms}) => (!isEmpty(amsPrograms)
        ? setChildText(
              rowChild,
              !isEmpty(get(last(sortBy(amsPrograms, 'lastUpdate')), 'title'))
                  ? get(last(sortBy(amsPrograms, 'lastUpdate')), 'title')
                  : 'N/A',
          )
        : rowChild);

const amsProgramEchostarId = (rowChild, {amsPrograms}) => (!isEmpty(amsPrograms) ? map(amsPrograms, 'echostarId').join(', ') : rowChild);

const shellEchostarId = (rowChild, {shellEchostarIds}) => (!isEmpty(shellEchostarIds) ? shellEchostarIds.join(', ') : rowChild);

export default {
    amsProgramEchostarId,
    amsProgramLastUpdate,
    amsProgramLastUpdateDetail,
    amsProgramTitle,
    issues,
    lastUpdated,
    lockedByIcon,
    lockedLink,
    mediaItemsTitle,
    moviesAndProgramsTitle,
    roles,
    shellEchostarId,
    timestamp,
};
